import TeamBase from './team-base';
import EventTeam from './team-event';
import Nomination from './nomination';
import User from '@/utils/user.js';
import teamService from '@/services/API/team/index';
import { rHandler } from '@/plugins/RHandler';
import {
  EVENT_TEAM_REQUEST_STATUS,
  EVENT_TEAM_REQUEST_STATUS_DATA,
  EVENT_TEAM_TRAINER_STATUS,
  EVENT_TEAM_TRAINER_STATUS_DATA,
} from '@/modules/Events/constants';
import { DEFAULT_NOMINATION } from '@/modules/UserEventLanding/constants';

/**
 * Модель заявки команды.
 * Тренер(!) выбирает мероприятие, которое имеет опцию 'is_for_trainer', и регистрируется на него;
 * То есть создает заявку (эту модель),
 * и к этой заявке привязывается команда, которая отображается у Организатора.
 * ----
 * Вкратце, Тренер видит эту модель, а Организатор видит обычную команду (модель 'team-event');
 * И эти две модели связаны полями team_id, team_name...
 */
export default class TeamEventRequest extends TeamBase {
  #user;

  constructor(team, url) {
    super(team, url);

    this.event = team.event || {};
    this.event_id = team.event_id;
    this.nomination = team.nomination ? new Nomination(team.nomination) : {};
    this.nomination_id = team.nomination_id;
    this.owner = team.owner || {};
    this.status = EVENT_TEAM_REQUEST_STATUS_DATA[team.status || EVENT_TEAM_REQUEST_STATUS.draft];

    this.request_status = EVENT_TEAM_TRAINER_STATUS_DATA[team.request_status || EVENT_TEAM_TRAINER_STATUS.STATUS_RECRUITMENT];
    this.team_id = team.team_id || null;
    this.team_name = team.team_name || null;
    this.team_description = team.team_description || null;
    this.comment = team.comment || null;
    this.team = team.team ? new EventTeam(team.team) : null;

    this.#user = new User();
  }

  get nominationName() {
    if (!this.nomination?.name) {
      return '';
    }

    if (this.nomination.name === DEFAULT_NOMINATION.NAME) {
      return DEFAULT_NOMINATION.NAME;
    }

    return this.nomination.name;
  }

  get isEditable() {
    return this.request_status.id === EVENT_TEAM_TRAINER_STATUS.STATUS_RECRUITMENT && this.#user.data.id === this.owner_id;
  }

  async addMembers(params, expandMembers = false) {
    const answer = await rHandler(
      teamService.addEventTeamRequestMembers(this.id, params),
      (response) => {
        this.setMembersModel(response.data?.data?.members);
        return { response };
      },
      (error) => ({ error }),
    );
    if (expandMembers) {
      await this.fetchMembersProfiles();
    }
    return { ...answer };
  }

  async removeMembers(params, expandMembers = false) {
    const answer = await rHandler(
      teamService.removeEventTeamRequestMembers(this.id, params),
      (response) => {
        this.setMembersModel(response.data?.data?.members);
        return { response };
      },
      (error) => ({ error }),
    );
    if (expandMembers) {
      await this.fetchMembersProfiles();
    }
    return { ...answer };
  }

  async setCaptain(params, expandMembers = false) {
    const answer = await rHandler(
      teamService.setCaptainEventTeamRequest(this.id, params),
      (response) => {
        this.setMembersModel(response.data?.data?.members);
        this.captain_id = params?.user_id || null;
        return { response };
      },
      (error) => ({ error }),
    );
    if (expandMembers) {
      await this.fetchMembersProfiles();
    }
    return { ...answer };
  }

  async sendRequest(params) {
    const data = await rHandler(
      teamService.sendEventTeamRequest(this.id, params),
      (response) => ({ response }),
      (error) => ({ error }),
    );
    return data;
  }

  // ДЛЯ ОРГАНИЗАТОРА, КОТОРЫЙ СМОТРИТ НА ЗАЯВКУ
  async check() {
    const data = await rHandler(
      teamService.incorrectEventTeamRequest(this.id),
      (response) => ({ response }),
      (error) => ({ error }),
    );
    return data;
  }

  async register(params) {
    // team_status -> active (2)
    const data = await rHandler(
      teamService.registerEventTeamRequest(this.id, params),
      (response) => ({ response }),
      (error) => ({ error }),
    );
    return data;
  }

  async reject(params) {
    // team_status -> reject (3)
    const data = await rHandler(
      teamService.rejectEventTeamRequest(this.id, params),
      (response) => ({ response }),
      (error) => ({ error }),
    );
    return data;
  }

  async incorrect(params) {
    // team_status -> incorrect documents (7)
    const data = await rHandler(
      teamService.incorrectEventTeamRequest(this.id, params),
      (response) => ({ response }),
      (error) => ({ error }),
    );
    return data;
  }

  async consider(params) {
    // team_status -> consider|request (1)
    const data = await rHandler(
      teamService.considerEventTeamRequest(this.id, params),
      (response) => ({ response }),
      (error) => ({ error }),
    );
    return data;
  }
}
